import { addDoc, collection, getDocs, Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { CACHE_KEYS, getCachedData } from "../services/localStorageService";
import PortalDropdown from "./common/PortalDropdown";
import SingleCheckButtons from "./common/SingleCheckButtons";

const ComplaintForm = ({ onClose, initialCategory }) => {
    const { user } = useAuth();
    const [complaintCount, setComplaintCount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("customer");
    const [categories, setCategories] = useState([]);
    const [complaintTypes, setComplaintTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isCategoryLocked, setIsCategoryLocked] = useState(false);

    const initialForm = {
        customerName: "",
        customerPhone: "",
        address: "",
        description: "",
        category: initialCategory || "",
        categoryId: "",
        complaintType: "",
        complaintTypeId: "",
        department: "",
        approximateResolveTime: "",
        // Red signal fields
        onu: { power: "", pon: "", lan: "" },
        router: { power: "", wanLan: "", wifi: "" },
        // CCTV fields
        cctv: {
            cameraCount: "",
            offlineChannels: "",
            dvrModel: "",
            recordingStatus: "",
            issueType: ""
        },
        // Billing fields
        billing: {
            billAmount: "",
            dueDate: "",
            paymentMethod: "",
            transactionId: "",
            lastDigits: ""
        },
        // Hardware fields
        hardware: {
            deviceType: "",
            modelNumber: "",
            serialNumber: "",
            warrantyStatus: "",
            issueType: ""
        },
        // Access Control fields
        accessControl: {
            deviceType: "",
            readerStatus: "",
            softwareVersion: "",
            issueType: ""
        }
    };

    const [form, setForm] = useState(initialForm);

    // Options
    // const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);
    const resolveTimeOptions = [
        // Even hours from 2 to 24
        ...Array.from({ length: 12 }, (_, i) => `${(i + 1) * 2} hours`),
        // Days
        "1 day",
        "2 days",
        "3 days",
        // Weeks
        "1 week"
    ];

    // Load data
    useEffect(() => {
        const loadData = () => {
            try {
                const categories = getCachedData(CACHE_KEYS.CATEGORIES);
                const types = getCachedData(CACHE_KEYS.TYPES);

                if (categories) {
                    setCategories(categories);

                    // Check if we have an initial category
                    if (initialCategory) {
                        setIsCategoryLocked(true);
                        const matchedCategory = categories.find(
                            cat => cat.name.toLowerCase() === initialCategory.toLowerCase()
                        );
                        if (matchedCategory) {
                            setSelectedCategory(matchedCategory);
                            setForm(prev => ({
                                ...prev,
                                category: matchedCategory.name,
                                categoryId: matchedCategory.id
                            }));
                        }
                    } else {
                        setIsCategoryLocked(false);
                    }
                }

                if (types) {
                    setComplaintTypes(types);
                }

                setLoading(false);
            } catch (error) {
                console.error("Error loading data from cache:", error);
                toast.error("Failed to load form data");
                setLoading(false);
            }
        };

        loadData();
    }, [initialCategory]);

    // Count existing complaints
    useEffect(() => {
        const fetchCount = async () => {
            try {
                const snapshot = await getDocs(collection(db, "complaints"));
                setComplaintCount(snapshot.size);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCount();
    }, []);

    // Handle normal input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle nested select changes
    const handleSelectChange = (name, value, group = null) => {
        if (group) {
            setForm((prev) => ({
                ...prev,
                [group]: { ...prev[group], [name]: value },
            }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    // Handle category selection (only when not locked)
    const handleCategorySelect = (name, value) => {
        console.log('Category select called with:', name, value);
        if (isCategoryLocked) return;

        const selectedCat = categories.find(cat => cat.id === value);
        if (selectedCat) {
            setSelectedCategory(selectedCat);
            setForm(prev => ({
                ...prev,
                category: selectedCat.name,
                categoryId: selectedCat.id,
                complaintType: "",
                complaintTypeId: "",
                // Reset technical fields
                onu: { power: "", pon: "", lan: "" },
                router: { power: "", wanLan: "", wifi: "" },
                cctv: { cameraCount: "", offlineChannels: "", dvrModel: "", recordingStatus: "", issueType: "" },
                billing: { billAmount: "", dueDate: "", paymentMethod: "", transactionId: "", lastDigits: "" },
                hardware: { deviceType: "", modelNumber: "", serialNumber: "", warrantyStatus: "", issueType: "" },
                accessControl: { deviceType: "", readerStatus: "", softwareVersion: "", issueType: "" }
            }));
            setActiveTab("office");
        }
    };

    // Handle complaint type change
    const handleComplaintTypeChange = (name, value) => {
        const selectedType = complaintTypes.find(type => type.id === value);
        setForm((prev) => ({
            ...prev,
            complaintType: selectedType?.name || "",
            complaintTypeId: value
        }));
    };

    // Get complaint types for selected category
    const getComplaintTypesForCategory = () => {
        if (!form.categoryId) return [];
        return complaintTypes.filter(type => type.categoryId === form.categoryId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.customerName || !form.customerPhone) {
            return toast.error("Please fill all required fields");
        }

        if (!form.categoryId) {
            return toast.error("Please select a category");
        }

        // Validate based on category
        const categoryId = form.categoryId;

        if (categoryId === "internet") {
            if (form.complaintType === "Red signal") {
                if (!form.onu.power || !form.onu.pon || !form.onu.lan) {
                    return toast.error("Please fill all ONU status fields for Red signal");
                }
                if (!form.router.power || !form.router.wanLan || !form.router.wifi) {
                    return toast.error("Please fill all Router status fields for Red signal");
                }
            }
        } else if (categoryId === "cctv") {
            if (!form.cctv.issueType) {
                return toast.error("Please fill CCTV issue type");
            }
        } else if (categoryId === "billing") {
            if (!form.billing.lastDigits) {
                return toast.error("Please fill lastDigits");
            }
        } else if (categoryId === "hardware") {
            if (!form.hardware.deviceType || !form.hardware.issueType) {
                return toast.error("Please fill hardware device and issue type");
            }
        } else if (categoryId === "access_control") {
            if (!form.accessControl.deviceType || !form.accessControl.issueType) {
                return toast.error("Please fill access control device and issue type");
            }
        }

        try {
            setSubmitting(true);
            const ticketNumber = `TICKET-${complaintCount + 1}`;

            const complaintData = {
                customerInfo: {
                    name: form.customerName,
                    phone: form.customerPhone,
                    address: form.address,
                },
                category: form.category,
                categoryId: form.categoryId,
                complaintType: form.complaintType,
                complaintTypeId: form.complaintTypeId,
                description: form.description,
                approximateResolveTime: form.approximateResolveTime,
                status: "pending",
                ticketNumber,
                createdBy: user.uid,
                createdAt: Timestamp.now(),
                activityLogs: [
                    {
                        time: Timestamp.now(),
                        message: "Complaint created",
                        userId: user.uid,
                    },
                ],
            };

            // Add category-specific data
            if (categoryId === "internet") {
                complaintData.onu = form.onu;
                complaintData.router = form.router;
            } else if (categoryId === "cctv") {
                complaintData.cctv = form.cctv;
            } else if (categoryId === "billing") {
                complaintData.billing = form.billing;
            } else if (categoryId === "hardware") {
                complaintData.hardware = form.hardware;
            } else if (categoryId === "access_control") {
                complaintData.accessControl = form.accessControl;
            }

            const docRef = await addDoc(collection(db, "complaints"), complaintData);
            const complaintUrl = `${window.location.origin}/complaint/${docRef.id}`;

            toast.success(`Complaint submitted successfully (Ticket: ${ticketNumber})`);
            onClose?.();
            setForm(initialForm);
            setComplaintCount((prev) => prev + 1);

            // Send SMS
            if (form.customerPhone) {
                const banglaMessage = `
প্রিয় ${form.customerName},

আপনার অভিযোগটি আমাদের সিস্টেমে গৃহীত হয়েছে।
আপনি নিচের লিঙ্ক থেকে অভিযোগের অবস্থা জানতে পারবেন:

${complaintUrl}

ধন্যবাদান্তে,
বিজয় আইটি সাপোর্ট টিম
`;

                await fetch("https://us-central1-ispadmin-7fb7e.cloudfunctions.net/sendSms", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        number: form.customerPhone.startsWith("88") ? form.customerPhone : `88${form.customerPhone}`,
                        message: banglaMessage,
                    }),
                });
            }

        } catch (err) {
            console.error(err);
            toast.error("Error submitting complaint");
        } finally {
            setSubmitting(false);
        }
    };

    // Check if Red signal is selected
    const isRedSignal = form.complaintType === "Red signal";
    const isCCTV = form.categoryId === "cctv";
    const isBilling = form.categoryId === "billing";
    const isHardware = form.categoryId === "hardware";
    const isAccessControl = form.categoryId === "access_control";

    const showTechnicalTab = isCCTV || isBilling || isHardware || isAccessControl || isRedSignal;

    const getCategoryIcon = () => {
        if (!selectedCategory) return '📌';
        const icons = {
            'internet': '🌐',
            'cctv': '📹',
            'billing': '💰',
            'hardware': '🖥️',
            'access_control': '🔐'
        };
        return icons[selectedCategory.id] || '📌';
    };

    const getCategoryColor = () => {
        if (!selectedCategory) return 'bg-blue-100 text-blue-800';
        const colors = {
            'internet': 'bg-blue-100 text-blue-800',
            'cctv': 'bg-indigo-100 text-indigo-800',
            'billing': 'bg-emerald-100 text-emerald-800',
            'hardware': 'bg-purple-100 text-purple-800',
            'access_control': 'bg-rose-100 text-rose-800'
        };
        return colors[selectedCategory.id] || 'bg-gray-100 text-gray-800';
    };

    // Render category-specific technical fields (same as before)
    const renderTechnicalFields = () => {
        if (isRedSignal) {
            return (
                <div className="space-y-8">
                    {/* ONU Status start */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">ONU Status</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Power *</label>
                                <SingleCheckButtons
                                    name="power"
                                    group="onu"
                                    options={["on", "off"]}
                                    value={form.onu.power}
                                    onChange={handleSelectChange}
                                />

                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">PON *</label>
                                <SingleCheckButtons
                                    name="pon"
                                    group="onu"
                                    options={["off", "green", "blinking", "red"]}
                                    value={form.onu?.pon}
                                    onChange={handleSelectChange}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">LAN *</label>
                                <SingleCheckButtons
                                    name="lan"
                                    group="onu"
                                    options={["off", "on"]}
                                    value={form.onu?.lan}
                                    onChange={handleSelectChange}
                                />
                            </div>
                        </div>
                    </div>
                    {/* onu end */}
                    {/* Router Status */}
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Router Status</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Power *</label>
                                <SingleCheckButtons
                                    name="power"
                                    group="router"
                                    options={["on", "off"]}
                                    value={form.router.power}
                                    onChange={handleSelectChange}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">WAN/LAN *</label>
                                <SingleCheckButtons
                                    name="wanLan"
                                    group="router"
                                    options={["wan", "lan"]}
                                    value={form.router.wanLan}
                                    onChange={handleSelectChange}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">WiFi *</label>
                                <SingleCheckButtons
                                    name="wifi"
                                    group="router"
                                    options={["Name changed", "same as previous", "connected no internet", "not connecting"]}
                                    value={form.router.wifi}
                                    onChange={handleSelectChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (isCCTV) {
            return (
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-100">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">CCTV Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Issue Type *</label>
                            <input
                                type="text"
                                name="issueType"
                                value={form.cctv.issueType}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "cctv")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Describe the CCTV issue"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Cameras</label>
                            <input
                                type="number"
                                name="cameraCount"
                                value={form.cctv.cameraCount}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "cctv")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Total cameras"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Offline Channels</label>
                            <input
                                type="text"
                                name="offlineChannels"
                                value={form.cctv.offlineChannels}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "cctv")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="e.g., 1,2,3"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">DVR/NVR Model</label>
                            <input
                                type="text"
                                name="dvrModel"
                                value={form.cctv.dvrModel}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "cctv")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="DVR model number"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (isBilling) {
            return (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V4m0 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Billing Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Payment number *</label>
                            <input
                                type="text"
                                name="lastDigits"
                                value={form.billing.lastDigits}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "billing")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="Enter last 2 or 3 digits"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bill Amount</label>
                            <input
                                type="number"
                                name="billAmount"
                                value={form.billing.billAmount}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "billing")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="Amount in BDT"
                            />
                        </div>
                        <div className="w-[30%]">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Paid On</label>
                            <input
                                type="date"
                                name="dueDate"
                                value={form.billing.dueDate}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "billing")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID</label>
                            <input
                                type="text"
                                name="transactionId"
                                value={form.billing.transactionId}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "billing")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="Transaction reference"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (isHardware) {
            return (
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Hardware Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Device Type *</label>
                            <input
                                type="text"
                                name="deviceType"
                                value={form.hardware.deviceType}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "hardware")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="e.g., Router, Switch, Server"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Issue Type *</label>
                            <input
                                type="text"
                                name="issueType"
                                value={form.hardware.issueType}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "hardware")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Describe the hardware issue"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Model Number</label>
                            <input
                                type="text"
                                name="modelNumber"
                                value={form.hardware.modelNumber}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "hardware")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Model number"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                            <input
                                type="text"
                                name="serialNumber"
                                value={form.hardware.serialNumber}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "hardware")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Serial number"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (isAccessControl) {
            return (
                <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-6 rounded-xl border border-rose-100">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Access Control Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Device Type *</label>
                            <input
                                type="text"
                                name="deviceType"
                                value={form.accessControl.deviceType}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "accessControl")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                                placeholder="e.g., Biometric, Card Reader"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Issue Type *</label>
                            <input
                                type="text"
                                name="issueType"
                                value={form.accessControl.issueType}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "accessControl")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                                placeholder="Describe the access control issue"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reader Status</label>
                            <input
                                type="text"
                                name="readerStatus"
                                value={form.accessControl.readerStatus}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "accessControl")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                                placeholder="Current status"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Software Version</label>
                            <input
                                type="text"
                                name="softwareVersion"
                                value={form.accessControl.softwareVersion}
                                onChange={(e) => handleSelectChange(e.target.name, e.target.value, "accessControl")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                                placeholder="Version number"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-600">Loading form...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-white">
                        {isCategoryLocked ? `New Complaint - ${initialCategory}` : 'New Complaint'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>


            <form onSubmit={handleSubmit} className="p-6">
                {/* Category Selection - Conditional */}
                <div className="mb-6">
                    {isCategoryLocked ? (
                        // Locked Category Display
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Complaint Category
                            </label>
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{getCategoryIcon()}</span>
                                <span className={`px-4 py-2 rounded-lg font-medium ${getCategoryColor()}`}>
                                    {form.category || 'Not selected'}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">
                                    (Category cannot be changed)
                                </span>
                            </div>
                        </div>
                    ) : (
                        // Category Selection Dropdown
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Complaint Category <span className="text-red-500">*</span>
                            </label>
                            <PortalDropdown
                                name="categoryId"
                                placeholder="Select a category"
                                options={categories.map(cat => ({
                                    value: cat.id,
                                    label: `${cat.icon || '📌'} ${cat.name}`
                                }))}
                                value={form.categoryId}
                                onChange={handleCategorySelect}
                                className="max-w-md"
                            />
                            {form.category && (
                                <p className="mt-2 text-xs text-gray-500">
                                    Selected: <span className="font-medium text-pink-600">{form.category}</span>
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Complaint Type Selection */}
                {form.categoryId && (
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Complaint Type <span className="text-red-500">*</span>
                        </label>

                        <PortalDropdown
                            name="complaintTypeId"
                            placeholder="Select Complaint type"
                            options={getComplaintTypesForCategory().map(type => ({
                                value: type.id,
                                label: type.name
                            }))}
                            value={form.complaintTypeId}
                            onChange={handleComplaintTypeChange}
                            className="max-w-md"
                        />

                        {form.complaintType && (
                            <p className="mt-2 text-xs text-gray-500">
                                Selected: <span className="font-medium text-pink-600">{form.complaintType}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Technical */}
                <div className="animate-fadeIn">
                    {renderTechnicalFields()}
                </div>

                {/* Customer Information */}
                <div className="space-y-6 mt-4 animate-fadeIn">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Customer Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="customerName"
                                    value={form.customerName}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                    placeholder="Enter customer name"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="customerPhone"
                                    value={form.customerPhone}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                                    placeholder="Enter phone number"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                        <textarea
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                            placeholder="Enter customer address"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                        placeholder="Describe the complaint in detail..."
                    />
                </div>
                {/* Form Actions */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex gap-3 w-full sm:w-auto">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 sm:flex-none px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </span>
                            ) : (
                                "Submit Complaint"
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 sm:flex-none px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>

                </div>
            </form>
        </div>
    );
};

export default ComplaintForm;